import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  User, 
  Calendar, 
  DollarSign, 
  CheckCircle, 
  Clock, 
  XCircle,
  Eye,
  FileText,
  TrendingUp,
  Filter,
  Search
} from 'lucide-react';

// Componentes reutilizables
import SearchBox from '../components/common/SearchBox';
import NotificationSystem, { useNotification } from '../components/common/NotificationSystem';
import CustomSelect from '../components/common/CustomSelect';

// Servicios
import serviceService from '../services/serviceService';
import salesService from '../services/salesService';

const ServiceSalesPage = () => {
  const navigate = useNavigate();
  const { notification, showNotification, hideNotification } = useNotification();

  // Estados
  const [serviceSales, setServiceSales] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    service: '',
    status: '',
    paymentStatus: '',
    dateFrom: '',
    dateTo: ''
  });

  // Cargar ventas por servicio
  const loadServiceSales = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await salesService.getSales({
        page,
        limit: itemsPerPage,
        search: searchTerm,
        status: filters.status,
        client: filters.client,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo
      });
      
      // Procesar las ventas para extraer servicios individuales
      const serviceSalesData = [];
      response.sales?.forEach(sale => {
        sale.items?.forEach((item, index) => {
          // Solo incluir si no hay filtro de servicio o si coincide
          if (!filters.service || item.service?._id === filters.service) {
            serviceSalesData.push({
              ...item,
              saleId: sale._id,
              saleNumber: sale.saleNumber,
              saleDate: sale.saleDate || sale.createdAt,
              saleStatus: sale.status,
              paymentStatus: sale.paymentStatus,
              paymentMethod: sale.paymentMethod,
              client: sale.client,
              notes: sale.notes
            });
          }
        });
      });
      
      setServiceSales(serviceSalesData);
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalItems(serviceSalesData.length);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error cargando ventas por servicio:', error);
      showNotification('Error al cargar las ventas por servicio', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters, itemsPerPage, showNotification]);

  // Cargar servicios
  const loadServices = useCallback(async () => {
    try {
      const response = await serviceService.getServices({
        page: 1,
        limit: 100,
        isActive: true
      });
      setServices(response.services || []);
    } catch (error) {
      console.error('Error cargando servicios:', error);
    }
  }, []);

  // Manejar filtros
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ 
      service: '', 
      status: '', 
      paymentStatus: '', 
      dateFrom: '', 
      dateTo: '' 
    });
    setCurrentPage(1);
  };

  // Navegación de páginas
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      loadServiceSales(newPage);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  // Manejar cambio de registros por página
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    loadServiceSales(1);
  };

  // Obtener estado de la venta
  const getSaleStatus = (status) => {
    const statusConfig = {
      'PENDING': { label: 'Pendiente', icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50' },
      'CONFIRMED': { label: 'Confirmada', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
      'COMPLETED': { label: 'Completada', icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-50' },
      'CANCELLED': { label: 'Cancelada', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' }
    };
    return statusConfig[status] || statusConfig['PENDING'];
  };

  // Obtener estado de pago
  const getPaymentStatus = (status) => {
    const statusConfig = {
      'PENDING': { label: 'Pendiente', color: 'text-yellow-600', bg: 'bg-yellow-50' },
      'PAID': { label: 'Pagado', color: 'text-green-600', bg: 'bg-green-50' },
      'PARTIAL': { label: 'Pago Parcial', color: 'text-blue-600', bg: 'bg-blue-50' },
      'OVERDUE': { label: 'Vencido', color: 'text-red-600', bg: 'bg-red-50' },
      'REFUNDED': { label: 'Reembolsado', color: 'text-purple-600', bg: 'bg-purple-50' }
    };
    return statusConfig[status] || statusConfig['PENDING'];
  };

  // Cargar datos iniciales
  useEffect(() => {
    loadServiceSales(1);
    loadServices();
  }, [loadServiceSales, loadServices]);

  // Recargar cuando cambien los filtros o búsqueda
  useEffect(() => {
    if (currentPage === 1) {
      loadServiceSales(1);
    } else {
      setCurrentPage(1);
      loadServiceSales(1);
    }
  }, [searchTerm, filters]);

  return (
    <div className="animate-fadeIn">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Package className="text-blue-600" size={32} />
            Servicios Vendidos
          </h1>
          <p className="text-gray-600 mt-1">Análisis detallado de cada servicio vendido por separado</p>
        </div>
      </div>

      {/* Contenedor de filtros */}
      <div className="card mb-6 filters-container">
        <div className="card-content">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Campo de búsqueda */}
            <div className="flex-1 min-w-64">
              <label className="form-label">Buscar</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cliente, número de factura..."
                className="form-input"
              />
            </div>

            {/* Filtro por servicio */}
            <div className="w-48">
              <CustomSelect
                label="Servicio"
                value={filters.service}
                onChange={(value) => handleFilterChange('service', value)}
                options={[
                  { value: '', label: 'Todos los servicios' },
                  ...services.map(service => ({ value: service._id, label: service.name }))
                ]}
                placeholder="Todos los servicios"
              />
            </div>

            {/* Filtro por estado de venta */}
            <div className="w-48">
              <CustomSelect
                label="Estado Venta"
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                options={[
                  { value: '', label: 'Todos los estados' },
                  { value: 'PENDING', label: 'Pendiente' },
                  { value: 'CONFIRMED', label: 'Confirmada' },
                  { value: 'COMPLETED', label: 'Completada' },
                  { value: 'CANCELLED', label: 'Cancelada' }
                ]}
                placeholder="Todos los estados"
              />
            </div>

            {/* Filtro por estado de pago */}
            <div className="w-48">
              <CustomSelect
                label="Estado Pago"
                value={filters.paymentStatus}
                onChange={(value) => handleFilterChange('paymentStatus', value)}
                options={[
                  { value: '', label: 'Todos los pagos' },
                  { value: 'PENDING', label: 'Pendiente' },
                  { value: 'PAID', label: 'Pagado' },
                  { value: 'PARTIAL', label: 'Pago Parcial' },
                  { value: 'OVERDUE', label: 'Vencido' },
                  { value: 'REFUNDED', label: 'Reembolsado' }
                ]}
                placeholder="Todos los pagos"
              />
            </div>

            {/* Filtro por fecha desde */}
            <div className="w-48">
              <label className="form-label">Fecha desde</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="form-input"
              />
            </div>

            {/* Filtro por fecha hasta */}
            <div className="w-48">
              <label className="form-label">Fecha hasta</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="form-input"
              />
            </div>

            {/* Botón limpiar */}
            <div className="w-48">
              <label className="form-label">&nbsp;</label>
              <button
                onClick={clearFilters}
                className="btn btn-outline w-full"
              >
                🗑️ Limpiar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Selector de registros por página */}
      <div className="records-per-page">
        <label htmlFor="itemsPerPage">Registros por página:</label>
        <select
          id="itemsPerPage"
          value={itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>

      {/* Lista de ventas por servicio */}
      <div className="table-container">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="loading-spinner"></div>
            <p className="mt-4 text-gray-600">Cargando ventas por servicio...</p>
          </div>
        ) : serviceSales.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || filters.service || filters.status || filters.paymentStatus || filters.dateFrom || filters.dateTo ? 'No se encontraron resultados' : 'No hay servicios vendidos'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filters.service || filters.status || filters.paymentStatus || filters.dateFrom || filters.dateTo ? 
                'No hay servicios que coincidan con los filtros aplicados' : 
                'No se han registrado servicios vendidos aún'
              }
            </p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Venta</th>
                <th>Cliente</th>
                <th>Servicio</th>
                <th>Cantidad</th>
                <th>Precio Unit.</th>
                <th>Total</th>
                <th>Estado Venta</th>
                <th>Estado Pago</th>
                <th>Fecha</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {serviceSales.map((item, index) => {
                const StatusIcon = getSaleStatus(item.saleStatus).icon;
                const statusConfig = getSaleStatus(item.saleStatus);
                const paymentStatusConfig = getPaymentStatus(item.paymentStatus);
                
                return (
                  <tr key={`${item.saleId}-${index}`}>
                    <td>
                      <div className="font-medium text-gray-900">
                        {item.saleNumber || `#${item.saleId?.slice(-6)}`}
                      </div>
                      <div className="text-sm text-gray-500">
                        Venta ID: {item.saleId?.slice(-6) || 'N/A'}
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-gray-400" />
                        <span className="font-medium">{item.client?.name || 'Cliente'}</span>
                      </div>
                      <div className="text-sm text-gray-500">{item.client?.email || ''}</div>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <Package size={16} className="text-gray-400" />
                        <span className="font-medium">{item.service?.name || 'Servicio'}</span>
                      </div>
                      <div className="text-sm text-gray-500">{item.service?.description || ''}</div>
                    </td>
                    <td>
                      <span className="font-medium">{item.quantity}</span>
                    </td>
                    <td>
                      <span className="text-gray-600">${item.unitPrice?.toLocaleString() || '0'}</span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 font-medium text-green-600">
                        <DollarSign size={16} />
                        ${item.totalPrice?.toLocaleString() || '0'}
                      </div>
                    </td>
                    <td>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                        <StatusIcon size={12} />
                        {statusConfig.label}
                      </span>
                    </td>
                    <td>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${paymentStatusConfig.bg} ${paymentStatusConfig.color}`}>
                        {paymentStatusConfig.label}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Calendar size={16} />
                        {new Date(item.saleDate).toLocaleDateString()}
                      </div>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button
                          onClick={() => navigate(`/sales/${item.saleId}`)}
                          className="btn btn-primary btn-sm"
                          title="Ver venta"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => navigate(`/sales/${item.saleId}`)}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b', color: 'white' }}
                          title="Ver detalle"
                        >
                          <TrendingUp size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>
              Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} de {totalItems} ventas por servicio
            </span>
          </div>
          <div className="pagination-controls">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="btn btn-outline btn-sm"
            >
              ← Anterior
            </button>
            
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-outline'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="btn btn-outline btn-sm"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {/* Sistema de notificaciones */}
      <NotificationSystem 
        notification={notification} 
        onClose={hideNotification} 
      />
    </div>
  );
};

export default ServiceSalesPage;
